import React, { useState } from 'react';
import type { KeyedMutator } from 'swr';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import type { Community, PointsConfig, ActivityPointItem } from '@/types';
import {
  ChartColumnIncreasing,
  FileHeartIcon,
  RotateCcw,
  Loader2,
  Flame,
  Users,
  Sparkles,
} from 'lucide-react';
import { HandsPrayingIcon } from '@phosphor-icons/react';

interface PointsSettingsProps {
  communityId: string;
  pointsConfig?: PointsConfig | null;
  mutateCommunity: KeyedMutator<{ community: Community }>;
}

const DEFAULT_POINTS: Required<PointsConfig> = {
  leadDirect: { giver: 0, receiver: 0 },
  thankYou: { giver: 0, receiver: 0 },
  testimonial: { giver: 0, receiver: 0 },
  meetup1on1: { giver: 0, receiver: 0 },
  meetupGroup: { giver: 0, receiver: 0 },
  meetupPower: { giver: 0, receiver: 0 },
};

export function PointsSettings({
  communityId,
  pointsConfig,
  mutateCommunity,
}: PointsSettingsProps) {
  const [config, setConfig] = useState<Required<PointsConfig>>(() => ({
    leadDirect: {
      giver: pointsConfig?.leadDirect?.giver ?? DEFAULT_POINTS.leadDirect.giver,
      receiver: pointsConfig?.leadDirect?.receiver ?? DEFAULT_POINTS.leadDirect.receiver,
    },
    thankYou: {
      giver: pointsConfig?.thankYou?.giver ?? DEFAULT_POINTS.thankYou.giver,
      receiver: pointsConfig?.thankYou?.receiver ?? DEFAULT_POINTS.thankYou.receiver,
    },
    testimonial: {
      giver: pointsConfig?.testimonial?.giver ?? DEFAULT_POINTS.testimonial.giver,
      receiver: pointsConfig?.testimonial?.receiver ?? DEFAULT_POINTS.testimonial.receiver,
    },
    meetup1on1: {
      giver: pointsConfig?.meetup1on1?.giver ?? DEFAULT_POINTS.meetup1on1.giver,
      receiver: pointsConfig?.meetup1on1?.receiver ?? DEFAULT_POINTS.meetup1on1.receiver,
    },
    meetupGroup: {
      giver: pointsConfig?.meetupGroup?.giver ?? DEFAULT_POINTS.meetupGroup.giver,
      receiver: pointsConfig?.meetupGroup?.receiver ?? DEFAULT_POINTS.meetupGroup.receiver,
    },
    meetupPower: {
      giver: pointsConfig?.meetupPower?.giver ?? DEFAULT_POINTS.meetupPower.giver,
      receiver: pointsConfig?.meetupPower?.receiver ?? DEFAULT_POINTS.meetupPower.receiver,
    },
  }));

  const [isSaving, setIsSaving] = useState(false);

  const handleValueChange = (
    key: keyof PointsConfig,
    field: 'giver' | 'receiver',
    value: string
  ) => {
    const numValue = Math.max(0, parseInt(value, 10) || 0);
    setConfig((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: numValue,
      },
    }));
  };

  const handleReset = () => {
    setConfig(DEFAULT_POINTS);
    toast.info('Points reset to defaults');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axiosInstance.patch(`/api/communities/${communityId}`, {
        pointsConfig: config,
      });
      toast.success('Points settings updated');
      await mutateCommunity();
    } catch (_error) {
      toast.error('Failed to update points settings');
    } finally {
      setIsSaving(false);
    }
  };

  const rows: {
    key: keyof PointsConfig;
    label: string;
    category?: string;
    icon: React.ReactNode;
  }[] = [
    {
      key: 'leadDirect',
      label: 'Lead (Direct)',
      category: 'Business & Networking',
      icon: <ChartColumnIncreasing className="w-5 h-5 text-primary" />,
    },
    {
      key: 'thankYou',
      label: 'Thank You',
      category: 'Recognition',
      icon: <HandsPrayingIcon className="w-5 h-5 text-primary" />,
    },
    {
      key: 'testimonial',
      label: 'Testimonial',
      category: 'Recognition',
      icon: <FileHeartIcon className="w-5 h-5 text-primary" />,
    },
    {
      key: 'meetup1on1',
      label: '1-2-1 Meetup',
      category: 'Meetup',
      icon: <Users className="w-5 h-5 text-primary" />,
    },
    {
      key: 'meetupGroup',
      label: 'Group Meetup',
      category: 'Meetup',
      icon: <Flame className="w-5 h-5 text-primary" />,
    },
    {
      key: 'meetupPower',
      label: 'Power Meetup',
      category: 'Meetup',
      icon: <Sparkles className="w-5 h-5 text-primary" />,
    },
  ];

  return (
    <Card className="-mx-2 sm:mx-0 rounded-2xl">
      <CardHeader className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle>Post & Activity Point Values</CardTitle>
            <CardDescription className="mt-1 font-normal">
              Specify point allocations for member engagement activities.
            </CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" size="sm" onClick={handleReset} className="w-full sm:w-auto">
              <RotateCcw className="w-4 h-4 mr-1.5" />
              Reset Defaults
            </Button>
            <Button onClick={handleSave} disabled={isSaving} size="sm" className="w-full sm:w-auto">
              {isSaving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {isSaving ? 'Saving...' : 'Save Points'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Desktop / Tablet Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <th className="py-3.5 px-6">Post / Activity</th>
                <th className="py-3.5 px-6 w-48 text-center">Giver Points</th>
                <th className="py-3.5 px-6 w-48 text-center">Receiver Points</th>
              </tr>
            </thead>
            <tbody className="divide-y text-sm">
              {rows.map((row) => {
                const item = config[row.key] as ActivityPointItem;
                return (
                  <tr key={row.key} className="hover:bg-muted/20 transition-colors">
                    <td className="py-4 px-6 align-middle">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                          {row.icon}
                        </div>
                        <div>
                          <span className="font-medium text-foreground block">{row.label}</span>
                          {row.category && (
                            <span className="text-xs text-muted-foreground">{row.category}</span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="py-4 px-6 align-middle">
                      <div className="flex justify-center">
                        <Input
                          type="number"
                          min={0}
                          value={item.giver}
                          onChange={(e) => handleValueChange(row.key, 'giver', e.target.value)}
                          className="text-center font-medium h-9 w-28"
                        />
                      </div>
                    </td>

                    <td className="py-4 px-6 align-middle">
                      <div className="flex justify-center">
                        <Input
                          type="number"
                          min={0}
                          value={item.receiver}
                          onChange={(e) => handleValueChange(row.key, 'receiver', e.target.value)}
                          className="text-center font-medium h-9 w-28"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Touch-Friendly Card View */}
        <div className="md:hidden divide-y divide-border border-t border-border">
          {rows.map((row) => {
            const item = config[row.key] as ActivityPointItem;
            return (
              <div key={row.key} className="p-4 sm:p-6 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                    {row.icon}
                  </div>
                  <div>
                    <span className="font-semibold text-foreground text-sm block">{row.label}</span>
                    {row.category && (
                      <span className="text-xs text-muted-foreground">{row.category}</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground block">
                      Giver Points
                    </label>
                    <Input
                      type="number"
                      min={0}
                      value={item.giver}
                      onChange={(e) => handleValueChange(row.key, 'giver', e.target.value)}
                      className="text-center font-medium h-9 w-full"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground block">
                      Receiver Points
                    </label>
                    <Input
                      type="number"
                      min={0}
                      value={item.receiver}
                      onChange={(e) => handleValueChange(row.key, 'receiver', e.target.value)}
                      className="text-center font-medium h-9 w-full"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
